import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { IonApp, IonContent, IonPage, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { AuthenticateWithRedirectCallback, useAuth, useUser } from './auth/useAuth';
import { CapacitorSignIn } from './components/auth/CapacitorSignIn';
import { AppTabBar } from './components/layout/AppTabBar';
import { HomeIndicator } from './components/layout/HomeIndicator';
import { safeAreaOffset, safeAreaPaddingX, withSafeArea } from './components/layout/safeArea';
import { PaywallProvider } from './components/subscription/usePaywall';
import AdminPage from './pages/AdminPage';
import AgeIneligiblePage from './pages/AgeIneligiblePage';
import DrawHistoryPage from './pages/DrawHistoryPage';
import HelpCenterPage from './pages/HelpCenterPage';
import HomePage from './pages/HomePage';
import OnboardingPage from './pages/OnboardingPage';
import PrivacyPage from './pages/PrivacyPage';
import RulesPage from './pages/RulesPage';
import ScanPage from './pages/ScanPage';
import SettingsPage from './pages/SettingsPage';
import StatsPage from './pages/StatsPage';
import TermsPage from './pages/TermsPage';
import TicketSnapshotsPage from './pages/TicketSnapshotsPage';
import { ruleBuilderService } from './services/ruleBuilderService';
import {
  configurePostPurchaseRefresh,
  configureServerProPoll,
  login as rcLogin,
  logOut as rcLogOut,
} from './services/purchasesService';
import { getChanceyApiToken } from './api/authToken';
import {
  getPushDebugState,
  init as pushInit,
  login as pushLogin,
  logout as pushLogout,
  setDeepLinkHandler,
} from './services/pushService';
import {
  flushAnalytics,
  setAnalyticsTokenProvider,
  track,
} from './services/analyticsService';
import { useSubscriptionStore } from './stores/subscriptionStore';
import { useGameApi } from './api/games';
import { useGameInfoStore } from './stores/gameInfoStore';
import { ChanceyThemeProvider, useChanceyTheme } from './theme/ThemeContext';
import { useUserPrefs } from './hooks/useUserPrefs';
import { isUnder18 } from './utils/age';

import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

import './theme/variables.css';

setupIonicReact();

const MainShell: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { c, dark } = useChanceyTheme();
  const { prefs } = useUserPrefs();
  const hideChrome = location.pathname.startsWith('/scan');

  // Install the push deep-link handler once MainShell mounts. Register here
  // (rather than in Gate or App) because only this subtree has `useHistory`
  // and we want the handler gone when the user signs out — MainShell's
  // unmount covers that.
  React.useEffect(() => {
    setDeepLinkHandler((route) => {
      if (!route || typeof route !== 'string') return;
      // Ionic's IonRouterOutlet honours `history.push` like any RR5 app,
      // so the same call routes correctly on both native and web.
      history.push(route);
    });
    return () => setDeepLinkHandler(null);
  }, [history]);

  React.useEffect(() => {
    track('screen_view', { screen: screenName(location.pathname) });
  }, [location.pathname]);

  // Hard 18+ block. The prefs hook hydrates from Clerk metadata before this runs, so we are
  // only under-18 here if we have a real birthday that says so. Missing birthday = no gate.
  if (isUnder18(prefs.birthday || undefined)) {
    return <AgeIneligiblePage />;
  }

  return (
    <div
      className="chancey-main-shell flex h-full w-full min-h-0 flex-col"
      style={{ backgroundColor: c.bg }}
    >
      <div className="relative min-h-0 min-w-0 flex-1">
        <IonRouterOutlet id="main-outlet">
          <Switch>
            <Route path="/home" component={HomePage} exact />
            <Route path="/home/draws" component={DrawHistoryPage} exact />
            <Route path="/home/tickets" component={TicketSnapshotsPage} exact />
            <Route path="/stats" component={StatsPage} exact />
            <Route path="/scan" component={ScanPage} exact />
            <Route path="/rules" component={RulesPage} exact />
            <Route path="/settings" component={SettingsPage} exact />
            <Route path="/admin" component={AdminPage} exact />
            <Route path="/help" component={HelpCenterPage} exact />
            <Route path="/terms" component={TermsPage} exact />
            <Route path="/privacy" component={PrivacyPage} exact />
            <Route exact path="/">
              <Redirect to="/home" />
            </Route>
            <Route render={() => <Redirect to="/home" />} />
          </Switch>
        </IonRouterOutlet>
      </div>
      {!hideChrome && (
        <footer className="chancey-tab-footer shrink-0" style={{ backgroundColor: c.bg }}>
          <AppTabBar />
          <HomeIndicator darkOrScan={dark} />
        </footer>
      )}
    </div>
  );
};

type SignInLocationState = { returnTo?: string };
const AUTH_RETURN_TO_KEY = 'chancey_auth_return_to';

const SignInGate: React.FC<{ allowGuestAccess?: boolean }> = ({
  allowGuestAccess = false,
}) => {
  const { c, typ, d, dark } = useChanceyTheme();
  const history = useHistory();
  const location = useLocation<SignInLocationState>();

  React.useEffect(() => {
    const returnTo = safeReturnTo(location.state?.returnTo);
    if (!returnTo) return;
    try {
      sessionStorage.setItem(AUTH_RETURN_TO_KEY, returnTo);
    } catch {
      /* signed-in fallback remains Home */
    }
  }, [location.state]);

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false} style={{ '--background': c.bg }}>
        <div
          className="chancey-signin-shell flex min-h-full flex-col"
          style={{ background: c.bg, fontFamily: typ.body }}
        >
          <header
            className="shrink-0 border-b pb-4"
            style={{
              ...safeAreaPaddingX(d.pad),
              borderColor: c.line,
              paddingTop: safeAreaOffset('top', 16, 52),
            }}
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <img
                src="/assets/icon/icon.png"
                alt=""
                aria-hidden="true"
                className="h-[80px] w-[80px] shrink-0 object-contain"
                style={{
                  filter: `drop-shadow(0 12px 18px ${c.accent}30)`,
                }}
              />
              <div className="min-w-0 max-w-[420px]">
                <div
                  className="text-[11px] font-semibold uppercase tracking-[0.12em]"
                  style={{ color: c.inkMuted, fontFamily: typ.body }}
                >
                  Chancey
                </div>
                <h1
                  className="mt-1 text-[26px] leading-[1.12]"
                  style={{
                    fontFamily: typ.display,
                    fontWeight: typ.displayWeight,
                    letterSpacing: typ.displayLetter,
                    color: c.ink,
                  }}
                >
                  Welcome back
                </h1>
                <p className="mt-1.5 text-[14px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                  Sign in to keep tickets and picks in sync across your devices.
                </p>
              </div>
            </div>
          </header>

          <div className="relative min-h-0 min-w-0 flex-1 overflow-y-auto">
            <div
              className="flex min-h-full flex-col items-center px-4 py-6"
              style={safeAreaPaddingX(Math.max(16, d.pad))}
            >
              <div className="w-full max-w-[400px] flex-1 py-2">
                {/*
                 * Custom OAuth-capable sign-in. We can't use Clerk's hosted
                 * `<SignIn>` component on native shells. Web OAuth still uses
                 * Clerk's current custom-flow API plus `/sso-callback` so the
                 * sign-in/sign-up transfer finalizes after Apple/Google.
                 */}
                <CapacitorSignIn />
                {allowGuestAccess && (
                  <button
                    type="button"
                    onClick={() => {
                      clearPendingReturnTo();
                      history.replace('/home');
                    }}
                    className="mt-4 w-full py-2 text-center text-[13px] font-semibold"
                    style={{ color: c.inkSoft, fontFamily: typ.body }}
                  >
                    Continue without an account
                  </button>
                )}
              </div>
            </div>
          </div>

          <footer className="shrink-0 border-t" style={{ borderColor: c.line, background: c.bg }}>
            <div style={safeAreaPaddingX(d.pad)} className="pt-2">
              <HomeIndicator darkOrScan={dark} />
              <p
                className="pt-1 text-center text-[11px] leading-relaxed"
                style={{
                  paddingBottom: `max(12px, ${withSafeArea('bottom')})`,
                  color: c.inkMuted,
                  fontFamily: typ.body,
                }}
              >
                Secure sign-in · Play responsibly · Must be 18+
              </p>
            </div>
          </footer>
        </div>
      </IonContent>
    </IonPage>
  );
};

const GuestShell: React.FC = () => {
  const { c } = useChanceyTheme();

  return (
    <div
      className="chancey-main-shell flex h-full w-full min-h-0 flex-col"
      style={{ backgroundColor: c.bg }}
    >
      <div className="relative min-h-0 min-w-0 flex-1">
        <IonRouterOutlet id="guest-outlet">
          <Switch>
            <Route path="/home" component={HomePage} exact />
            <Route
              path="/sign-in"
              render={() => <SignInGate allowGuestAccess />}
              exact
            />
            <Route path="/terms" component={TermsPage} exact />
            <Route path="/privacy" component={PrivacyPage} exact />
            <Route exact path="/">
              <Redirect to="/home" />
            </Route>
            <Route
              render={({ location }) => (
                <Redirect
                  to={{
                    pathname: '/sign-in',
                    state: { returnTo: location.pathname },
                  }}
                />
              )}
            />
          </Switch>
        </IonRouterOutlet>
      </div>
    </div>
  );
};

const AuthenticatedShell: React.FC = () => {
  const location = useLocation<SignInLocationState>();
  const returnTo = pendingReturnTo(location);

  React.useEffect(() => {
    if (!returnTo || location.pathname === returnTo) clearPendingReturnTo();
  }, [location.pathname, returnTo]);

  if (returnTo && location.pathname !== returnTo) {
    return <Redirect to={returnTo} />;
  }
  return <MainShell />;
};

const Gate: React.FC = () => {
  const { onboardingComplete } = useChanceyTheme();
  const { prefs } = useUserPrefs();
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { user } = useUser();
  const userId = user?.id;
  const gameId = prefs.gameId;
  const { getGameInfo, getRulesCatalog } = useGameApi();
  const loadGameInfo = useGameInfoStore((s) => s.load);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    void ruleBuilderService.syncCatalogFromApi(getRulesCatalog);
  }, [isLoaded, isSignedIn, getRulesCatalog]);

  // Keep a ref to the live Clerk user / getToken so the post-purchase callback
  // closure doesn't go stale when Clerk re-renders. Using refs (instead of
  // putting `user` or `getToken` in the effect deps) avoids the common Clerk
  // churn that would otherwise run `rcLogin` on every render.
  const userRef = React.useRef(user);
  userRef.current = user;
  const getTokenRef = React.useRef(getToken);
  getTokenRef.current = getToken;
  // Track the userId we've already pushed the initial OneSignal sync for so
  // the effect below fires exactly once per sign-in, not on every pref toggle.
  // A ref (rather than effect deps on prefs.notif) avoids the double-POST
  // with SettingsPage's own per-toggle /v1/me/push/prefs writer.
  const pushSyncedForUserId = React.useRef<string | null>(null);
  // Mirror the live prefs so the one-shot sync can read the freshest value at
  // the moment the user signs in (useUserPrefs hydrates from Clerk
  // asynchronously, so a captured closure can be stale).
  const prefsRef = React.useRef(prefs);
  prefsRef.current = prefs;
  const analyticsUserRef = React.useRef<string | null>(null);
  const analyticsGameRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    setAnalyticsTokenProvider(() => getChanceyApiToken(getToken));
    return () => setAnalyticsTokenProvider(null);
  }, [getToken]);

  React.useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn || !userId) {
      analyticsUserRef.current = null;
      void flushAnalytics();
      return;
    }
    if (analyticsUserRef.current !== userId) {
      analyticsUserRef.current = userId;
      track('auth_success');
      void flushAnalytics();
    }
  }, [isLoaded, isSignedIn, userId]);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (analyticsGameRef.current === null) {
      analyticsGameRef.current = gameId;
      return;
    }
    if (analyticsGameRef.current !== gameId) {
      analyticsGameRef.current = gameId;
      track('game_change', { gameId });
    }
  }, [gameId, isLoaded, isSignedIn]);

  // Wire RevenueCat identity to the Clerk user id, and install the post-purchase
  // hook that forces a fresh JWT so the server sees `pro=true` on the next call.
  React.useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn || !userId) {
      // On sign-out, clear RC + OneSignal identity so a different user
      // logging in on the same device doesn't inherit the previous user's
      // entitlement cache or notification subscription. Both are
      // fire-and-forget: the app state reset below is the important part.
      void rcLogOut();
      void pushLogout();
      useSubscriptionStore.setState({
        status: 'unknown',
        isPro: false,
        customerInfo: null,
        offerings: null,
        scansUsedThisMonth: null,
        scansCapThisMonth: null,
        scansResetAtUtc: null,
      });
      return;
    }
    configurePostPurchaseRefresh(async () => {
      try {
        await userRef.current?.reload();
      } catch {
        /* non-fatal: getToken skipCache below still forces a fresh JWT */
      }
      try {
        await getChanceyApiToken(getTokenRef.current, { skipCache: true });
      } catch {
        /* API client will try again next request */
      }
    });
    configureServerProPoll(async () => {
      const base = process.env.REACT_APP_API_BASE_URL ?? '';
      if (!base) return false;
      let token: string | null = null;
      token = await getChanceyApiToken(getTokenRef.current, { skipCache: true });
      if (!token) return false;
      try {
        const res = await fetch(`${base}/v1/me/subscription`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (!res.ok) return false;
        const body = (await res.json()) as { pro?: boolean };
        return body.pro === true;
      } catch {
        return false;
      }
    });

    void (async () => {
      try {
        await rcLogin(userId);
        await useSubscriptionStore.getState().refresh();
      } catch {
        /* RC SDK failures must not block the app; subscriptionStore.error
           surfaces the message when the user opens the paywall. */
      }
    })();

  }, [isLoaded, isSignedIn, userId]);

  // OneSignal identity + one-shot pref sync per sign-in. `pushLogin` is a
  // no-op when the feature flag is unset.
  //
  // The trailing `POST /v1/me/push/prefs` seeds the server from the user's
  // local prefs the FIRST time we see them signed in (e.g. they just
  // completed onboarding). Subsequent toggles are owned by SettingsPage;
  // running this on every pref change would duplicate those POSTs.
  React.useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId) {
      pushSyncedForUserId.current = null;
      return;
    }
    if (pushSyncedForUserId.current === userId) return;
    pushSyncedForUserId.current = userId;
    void (async () => {
      try {
        await pushInit();
        await pushLogin(userId);
      } catch {
        /* push is a nice-to-have; never block the main flow */
      }

      const base = process.env.REACT_APP_API_BASE_URL ?? '';
      if (!base) return;
      let token: string | null = null;
      token = await getChanceyApiToken(getTokenRef.current);
      if (!token) return;
      // UserPrefsProvider hydrates prefs from D1 after sign-in. Until that
      // finishes, local prefs preserve onboarding choices made before auth.
      const notif = prefsRef.current.notif;
      let subscriptionId: string | null | undefined;
      try {
        const pushDebug = await getPushDebugState();
        subscriptionId =
          pushDebug.subscriptionId &&
          pushDebug.subscriptionTokenPresent &&
          pushDebug.optedIn !== false
            ? pushDebug.subscriptionId
            : null;
      } catch {
        subscriptionId = null;
      }
      try {
        const res = await fetch(`${base}/v1/me/push/prefs`, {
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            draws: !!notif.draws,
            jackpot: !!notif.jackpot,
            results: !!notif.results,
            weekly: !!notif.weekly,
            ...(subscriptionId ? { subscriptionId } : {}),
          }),
        });
        // Transport failures and partial server-side sync failures can leave
        // OneSignal tags missing. Allow a retry on the next sign-in rather
        // than permanently marking this userId synced.
        if (!res.ok) {
          pushSyncedForUserId.current = null;
          return;
        }
        try {
          const body = (await res.json()) as { ok?: boolean };
          if (body.ok === false) pushSyncedForUserId.current = null;
        } catch {
          /* Older successful responses may not have JSON; HTTP 2xx is enough. */
        }
      } catch {
        pushSyncedForUserId.current = null;
      }
    })();
  }, [isLoaded, isSignedIn, userId]);

  // Load game info/history once auth is ready + signed in, and refresh on game change.
  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    void loadGameInfo(gameId, { getGameInfo }, 20);
  }, [gameId, getGameInfo, isLoaded, isSignedIn, loadGameInfo]);

  if (!onboardingComplete) {
    return <OnboardingPage />;
  }
  if (!isLoaded) {
    return null;
  }
  if (isSignedIn) {
    return <AuthenticatedShell />;
  }
  return Capacitor.getPlatform() === 'ios' ? <GuestShell /> : <SignInGate />;
};

const OAuthCallbackPage: React.FC = () => {
  const { c, typ } = useChanceyTheme();

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false} style={{ '--background': c.bg }}>
        <div
          className="flex min-h-full items-center justify-center px-6 text-center text-sm"
          style={{ color: c.inkSoft, fontFamily: typ.body }}
        >
          Completing sign-in…
          <AuthenticateWithRedirectCallback
            signInForceRedirectUrl="/home"
            signInFallbackRedirectUrl="/home"
            signUpForceRedirectUrl="/home"
            signUpFallbackRedirectUrl="/home"
            signInUrl="/"
            signUpUrl="/"
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

const App: React.FC = () => {
  return (
    <ChanceyThemeProvider>
      <IonApp>
        <IonReactRouter>
          <PaywallProvider>
            <Switch>
              <Route path="/sso-callback" component={OAuthCallbackPage} exact />
              <Route component={Gate} />
            </Switch>
          </PaywallProvider>
        </IonReactRouter>
      </IonApp>
    </ChanceyThemeProvider>
  );
};

export default App;

function screenName(pathname: string): string {
  if (pathname === '/' || pathname === '/home') return 'home';
  return pathname.replace(/^\//, '').replace(/\//g, '_') || 'home';
}

function pendingReturnTo(location: { state?: SignInLocationState }): string | null {
  const fromState = safeReturnTo(location.state?.returnTo);
  if (fromState) return fromState;
  try {
    return safeReturnTo(sessionStorage.getItem(AUTH_RETURN_TO_KEY));
  } catch {
    return null;
  }
}

function clearPendingReturnTo(): void {
  try {
    sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
  } catch {
    /* ignore */
  }
}

function safeReturnTo(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  const allowed = ['/home', '/stats', '/scan', '/rules', '/settings', '/help'];
  return allowed.some((path) => value === path || value.startsWith(`${path}/`)) ? value : null;
}
